"use client";

import { useState } from "react";
import { trackLead } from "@/lib/analytics";

// Generisk e-postfangst mot de to beviste magnet-rutene:
//  - smakebit  → /api/smakebit        (7 middager under 500 kcal, full drip D2–D43)
//  - trening   → /api/gratis-trening  (smakebit av 12-ukers program per track, trening-drip)
// Samtykkemønsteret er det samme som RecipeEmailCta: verdibyttet står i klartekst
// ved knappen, så innsending er informert, aktivt samtykke (ingen checkbox).

type TreningTrack = "hjemme" | "gym" | "lop";

const TRENING_COPY: Record<TreningTrack, { title: string; kort: string }> = {
  hjemme: { title: "Styrke hjemme/ute — 12 ukers program uten utstyr", kort: "hjemmeprogrammet" },
  gym: { title: "Gym-programmet — 12 ukers styrkeprogram", kort: "gym-programmet" },
  lop: { title: "Løpeprogrammet — 12 uker med riktig progresjon", kort: "løpeprogrammet" },
};

interface Props {
  magnet: "smakebit" | "trening";
  track?: TreningTrack;
  source: string;
}

export function MagnetCapture({ magnet, track = "gym", source }: Props) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const erTrening = magnet === "trening";
  const t = TRENING_COPY[track];

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(erTrening ? "/api/gratis-trening" : "/api/smakebit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(
          erTrening
            ? { email, name, consent: true, track, utm: { source } }
            : { email, name, consent: true, utm: { source } }
        ),
      });
      if (!res.ok) throw new Error("Noe gikk galt");
      trackLead("magnet_capture", { content_name: source, method: magnet });
      setDone(true);
    } catch {
      setError("Noe gikk galt — prøv igjen, eller send oss en e-post på hei@coachguiden.no.");
    } finally {
      setLoading(false);
    }
  }

  if (done) {
    return (
      <div className="mt-10 rounded-3xl border border-stone-200/70 bg-gradient-to-br from-amber-50/80 via-orange-50/40 to-stone-50 p-7 sm:p-9 text-center shadow-sm">
        <h3 className="text-lg font-semibold text-stone-900 mb-1.5">
          {erTrening ? "Smakebiten er på vei!" : "Ukesmenyen er på vei!"}
        </h3>
        <p className="text-sm text-stone-600 max-w-md mx-auto">
          Sjekk innboksen — kanskje også søppelposten første gang.{" "}
          {erTrening ? `Du får en gratis smakebit av ${t.kort} som PDF.` : "Du får 7 middager under 500 kcal med ferdig handleliste."}
        </p>
      </div>
    );
  }

  return (
    <div className="mt-10 rounded-3xl border border-stone-200/70 bg-gradient-to-br from-amber-50/80 via-orange-50/40 to-stone-50 p-7 sm:p-9 shadow-sm relative overflow-hidden">
      <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-gradient-to-br from-amber-200/40 to-orange-200/20 blur-3xl pointer-events-none" aria-hidden="true" />
      <div className="relative z-10">
        <div className="flex items-center gap-2 mb-3">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/80 border border-stone-200/60 shadow-sm" aria-hidden="true">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M3 8l9 6 9-6M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" stroke="#57534e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <span className="text-xs font-medium text-stone-700 uppercase tracking-wide">
            {erTrening ? "Gratis smakebit" : "Gratis ukesmeny"}
          </span>
        </div>
        <h3 className="text-xl sm:text-2xl font-bold text-stone-900 leading-tight mb-2">
          {erTrening ? (
            <>Få en gratis smakebit av {t.kort} <span className="italic text-stone-500 font-normal">på e-post</span></>
          ) : (
            <>Få 7 middager under 500 kcal <span className="italic text-stone-500 font-normal">gratis på e-post</span></>
          )}
        </h3>
        <p className="text-sm text-stone-600 mb-5 max-w-lg">
          {erTrening
            ? `${t.title} — du får en smakebit som PDF rett i innboksen, pluss konkrete treningstips fra CoachGuiden.`
            : "En ferdig ukesmeny som PDF — med handleliste og næringsinnhold per porsjon. Perfekt om du vil spise sunnere uten å telle hver eneste kalori."}
        </p>

        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input
              type="text"
              placeholder="Fornavn (valgfritt)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-white/80 backdrop-blur-sm border border-stone-200/70 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-stone-400 placeholder:text-stone-400"
            />
            <input
              type="email"
              required
              placeholder="din@epost.no"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-white/80 backdrop-blur-sm border border-stone-200/70 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-stone-400 placeholder:text-stone-400"
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-2.5 items-stretch sm:items-center pt-1">
            <button
              type="submit"
              disabled={!email || loading}
              className="px-6 py-3 bg-stone-900 text-white rounded-xl text-sm font-semibold hover:bg-stone-800 transition-all shadow-sm shadow-stone-900/10 disabled:opacity-50 disabled:cursor-not-allowed flex-1 sm:flex-initial"
            >
              {loading ? "Sender …" : erTrening ? "Send meg programmet →" : "Send meg ukesmenyen →"}
            </button>
            <p className="text-xs text-stone-400 sm:ml-3">Vi deler aldri e-posten din.</p>
          </div>

          <p className="text-xs text-stone-500 leading-relaxed pt-0.5 max-w-lg">
            {erTrening
              ? "Du får smakebiten med én gang — pluss treningstips fra CoachGuiden. Du kan melde deg av når som helst."
              : "Du får ukesmenyen med én gang — pluss nye oppskrifter og kostholds-tips fra CoachGuiden. Du kan melde deg av når som helst."}
          </p>
        </form>

        {error && (
          <div className="mt-3 rounded-xl bg-red-50 border border-red-200 px-3.5 py-2.5 text-xs text-red-700">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
